// For-loop block parser and token finder for scanner-based expansion.
// Example token: @for(i, var(--scale)) { ... }.
import type { ForLoopParseResult } from './types'
import type { LuisArrayVariables } from '../../types'
import type { LuisObjectVariables } from '../../types'
import { isArrayVariableValue, parseArrayVariable } from '../../variables/array-variable'
import { findMatchingPair, isWhitespace, isWordLike, skipWhitespace, splitTopLevelByComma } from './scanner-utils'
import { parseLoopTargetArgument } from './loop-target'

// Locate the next `for(` or `@for(` token from the cursor position.
export function findNextForLoopStart(source: string, fromIndex: number): number {
  for (let index = fromIndex; index < source.length; index += 1) {
    const matchLength = matchForTokenAt(source, index)
    // Continue scan until parser sees a valid loop token prefix.
    if (matchLength === 0) {
      continue
    } else {
      // Return loop start index on first token match.
      return index
    }
  }

  // Return -1 when no loop token is found.
  return -1
}

// Parse one for-loop block and return body boundaries.
export function parseForLoop(source: string, startIndex: number): ForLoopParseResult | null {
  const keywordLength = matchForTokenAt(source, startIndex)
  // Return null when token is not a valid loop prefix.
  if (keywordLength === 0) {
    return null
  } else {
    // Continue parsing loop header.
  }

  let cursor = startIndex + keywordLength
  cursor = skipWhitespace(source, cursor)
  // Reject malformed loops without opening `(`.
  if (source[cursor] !== '(') {
    return null
  } else {
    // Continue parsing loop argument list.
  }

  const argsEnd = findMatchingPair(source, cursor, '(', ')')
  // Reject loops when argument list is not balanced.
  if (argsEnd === -1) {
    return null
  } else {
    // Continue parsing resolved loop arguments.
  }

  const argsSource = source.slice(cursor + 1, argsEnd)
  const args = splitTopLevelByComma(argsSource).map((entry) => entry.trim())
  // Require exactly 2 arguments: iterator name and source expression.
  if (args.length !== 2) {
    return null
  } else {
    // Continue validating loop variable and source expression.
  }

  const loopTarget = parseLoopTargetArgument(args[0])
  // Reject invalid loop target forms such as malformed tuples.
  if (!loopTarget) {
    return null
  } else {
    // Continue parsing loop body block.
  }

  cursor = skipWhitespace(source, argsEnd + 1)
  // Reject malformed loops without body block opener.
  if (source[cursor] !== '{') {
    return null
  } else {
    // Continue matching loop body braces.
  }

  const bodyEnd = findMatchingPair(source, cursor, '{', '}')
  // Reject loops when body braces are not balanced.
  if (bodyEnd === -1) {
    return null
  } else {
    // Return fully parsed loop block metadata.
    return {
      variableName: loopTarget.variableName,
      indexVariableName: loopTarget.indexVariableName,
      sourceExpression: args[1],
      body: source.slice(cursor + 1, bodyEnd),
      endIndex: bodyEnd + 1,
    }
  }
}

// Resolve iterator expression from `var(--array)` or direct `[ ... ]` list.
export function resolveLoopValues(
  sourceExpression: string,
  arrays: LuisArrayVariables,
  objectVariables: LuisObjectVariables,
): string[] | null {
  const trimmed = sourceExpression.trim()
  const variableRef = trimmed.match(/^var\(\s*(--[a-zA-Z_][\w-]*)(?:\s*\[\s*([^\]\s]+)\s*\])?\s*\)$/)

  // Resolve values from declared array variable when expression uses `var(...)`.
  if (variableRef) {
    const variableValues = arrays[variableRef[1]]
    const objectValue = objectVariables[variableRef[1]]
    // Resolve array item when token uses numeric bracket access.
    if (variableValues && typeof variableRef[2] === 'string' && /^\d+$/.test(variableRef[2])) {
      // Resolve single array item when loop source includes bracket index.
      const index = Number.parseInt(variableRef[2], 10)
      const indexedValue = variableValues[index]
      return typeof indexedValue === 'string' ? [indexedValue] : null
    } else if (variableValues && typeof variableRef[2] !== 'string') {
      // Return resolved array variable values.
      return variableValues
    } else if (objectValue && typeof variableRef[2] !== 'string') {
      // Return object keys when loop source iterates over object variable.
      return Object.keys(objectValue)
    } else {
      // Return null for unknown variable names or unsupported keyed object loop sources.
      return null
    }
  } else {
    // Continue with inline array resolution.
  }

  // Parse direct inline arrays like `[1,2,3]` or `[A-F]`.
  if (isArrayVariableValue(trimmed)) {
    return parseArrayVariable(trimmed)
  } else {
    // Return null when source expression is not an array.
    return null
  }
}

// Match loop token and return consumed keyword length.
export function matchForTokenAt(source: string, index: number): number {
  const withAt = source.slice(index, index + 4) === '@for'
  const withoutAt = source.slice(index, index + 3) === 'for'
  // Reject positions that do not start with loop keyword.
  if (!withAt && !withoutAt) {
    return 0
  } else {
    // Continue loop token boundary validation.
  }

  const keywordLength = withAt ? 4 : 3
  const before = index > 0 ? source[index - 1] : ''
  const after = source[index + keywordLength] ?? ''
  // Enforce token boundaries to avoid matching inside words.
  if (isWordLike(before) || (!isWhitespace(after) && after !== '(')) {
    return 0
  } else {
    // Return valid keyword length for matched loop token.
    return keywordLength
  }
}
